<template>
    <div class="photography-venue-picker">
        <el-popover
            ref="popover"
            v-model="visible"
            placement="bottom-start"
            trigger="click"
            popper-class="photography-venue-popper"
            :append-to-body="false"
            @show="resetPicker">
            <div class="venue-picker-head">
                <button v-if="pickerState.depth > 0" type="button" @click="goBack">
                    <i class="el-icon-arrow-left"></i> 上一级
                </button>
                <span>{{leftLevelName}}</span>
                <i class="el-icon-arrow-right"></i>
                <span>{{rightLevelName}}</span>
            </div>
            <div class="venue-picker-columns">
                <div class="venue-picker-column" role="listbox" :aria-label="leftLevelName">
                    <button
                        v-for="node in pickerState.leftNodes"
                        :key="node.value"
                        type="button"
                        :class="{active: node.value === pickerState.activeLeftValue}"
                        @click="chooseLeft(node)">
                        <span>{{node.label}}</span>
                        <span v-if="node.campus" class="campus-venue-badge">校园</span>
                        <i class="el-icon-arrow-right"></i>
                    </button>
                </div>
                <div class="venue-picker-column" role="listbox" :aria-label="rightLevelName">
                    <button
                        v-for="node in pickerState.rightNodes"
                        :key="node.value"
                        type="button"
                        :class="{active: pickerState.selection && pickerState.selection[pickerState.selection.length - 1] === node.value}"
                        @click="chooseRight(node)">
                        <span>{{node.label}}</span>
                        <span v-if="node.campus" class="campus-venue-badge">校园</span>
                        <i v-if="node.children && node.children.length" class="el-icon-arrow-right"></i>
                    </button>
                    <p v-if="!pickerState.rightNodes.length" class="venue-picker-empty">暂无下一级可选项</p>
                </div>
            </div>
            <el-input
                slot="reference"
                :value="selectedLabel"
                readonly
                placeholder="请选择拍摄场地">
                <template slot="suffix">
                    <span v-if="isCampusVenue" class="campus-venue-badge reference">校园</span>
                    <i class="el-input__icon el-icon-arrow-down"></i>
                </template>
            </el-input>
        </el-popover>
    </div>
</template>

<script>
import photographyBooking from "@/tool/photographyBooking.cjs";

const {
    advancePhotographyVenuePicker,
    createPhotographyVenuePickerState,
    resolvePhotographyVenueSelection,
    retreatPhotographyVenuePicker,
    selectPhotographyVenuePickerLeft,
} = photographyBooking;

export default {
    name: "PhotographyVenuePicker",
    props: {
        value: { type: Array, default: () => [] },
        venues: { type: Array, default: () => [] },
    },
    data() {
        return {
            visible: false,
            pickerState: createPhotographyVenuePickerState(this.venues),
        };
    },
    computed: {
        selectedLabel() {
            const selection = resolvePhotographyVenueSelection(this.value, this.venues);
            if (!selection) return "";
            return [selection.venueName, selection.pointName].filter(Boolean).join(" · ");
        },
        isCampusVenue() {
            const selection = resolvePhotographyVenueSelection(this.value, this.venues);
            if (!selection) return false;
            const venue = (this.venues || []).find((item) => String(item.id) === String(selection.venueId));
            return !!(venue && venue.campusQualificationRequired);
        },
        leftLevelName() {
            const nodes = this.pickerState.leftNodes;
            if (nodes.some(node => node.value === 'campus')) return '校园 / 市';
            const type = ((nodes[0] || {}).value || '').split(':')[0];
            return { province: '省', city: '市', district: '区', venue: '场地', point: '地点' }[type] || '省';
        },
        rightLevelName() {
            const nodes = this.pickerState.rightNodes;
            if (nodes.some(node => node.value === 'campus')) return '校园 / 市';
            const type = ((nodes[0] || {}).value || '').split(':')[0];
            return { city: '市', district: '区', venue: '场地', point: '地点' }[type] || '下一级';
        },
    },
    watch: {
        venues: {
            deep: true,
            handler() { this.resetPicker(); },
        },
    },
    methods: {
        resetPicker() {
            this.pickerState = createPhotographyVenuePickerState(this.venues);
        },
        chooseLeft(node) {
            this.pickerState = selectPhotographyVenuePickerLeft(this.pickerState, node.value);
            if (this.pickerState.selection) {
                this.$emit('input', this.pickerState.selection);
                this.$emit('change', this.pickerState.selection);
            }
        },
        chooseRight(node) {
            const next = advancePhotographyVenuePicker(this.pickerState, node.value);
            this.pickerState = next;
            if (!next.selection) return;
            this.$emit("input", next.selection);
            this.$emit("change", next.selection);
        },
        goBack() {
            this.pickerState = retreatPhotographyVenuePicker(this.pickerState);
        },
    },
};
</script>

<style scoped>
.photography-venue-picker{ width:100%; }
.photography-venue-picker /deep/ .el-popover__reference-wrapper{ display:block; width:100%; }
.photography-venue-picker /deep/ .el-input{ width:100%; }
</style>

<style>
.photography-venue-popper{ width:460px; max-width:calc(100vw - 32px); padding:0; overflow:hidden; }
.venue-picker-head{ display:flex; min-height:42px; align-items:center; gap:8px; padding:0 12px; color:#8a8fa0; border-bottom:1px solid #ebeef5; font-size:12px; }
.venue-picker-head button{ margin-right:auto; padding:0; color:#6670d8; background:none; border:0; cursor:pointer; }
.venue-picker-columns{ display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); min-height:210px; max-height:320px; }
.venue-picker-column{ overflow:auto; padding:6px 0; }
.venue-picker-column+ .venue-picker-column{ border-left:1px solid #ebeef5; }
.venue-picker-column button{ display:flex; width:100%; min-height:38px; align-items:center; gap:7px; padding:8px 12px; color:#606266; text-align:left; background:#fff; border:0; cursor:pointer; }
.venue-picker-column button:hover,.venue-picker-column button.active{ color:#5f67d8; background:#f3f5ff; }
.venue-picker-column button>span:first-child{ min-width:0; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.venue-picker-column button>i{ flex:0 0 auto; color:#c0c4cc; }
.venue-picker-empty{ margin:70px 12px 0; color:#a5a9b5; text-align:center; font-size:12px; }
.photography-venue-picker /deep/ .el-input__suffix-inner{ display:inline-flex; align-items:center; gap:4px; }
.photography-venue-popper .campus-venue-badge,
.photography-venue-picker .campus-venue-badge{ display:inline-flex; min-height:20px; align-items:center; padding:1px 7px; color:#a94711; background:#fff1d6; border:1px solid #ffc56b; border-radius:999px; font-size:11px; font-weight:700; line-height:16px; }
.photography-venue-picker .campus-venue-badge.reference{ margin-right:1px; }
@media (max-width:560px){ .photography-venue-popper{ width:calc(100vw - 32px); }.venue-picker-columns{ min-height:190px; } }
</style>
